<template>
    <div :class="classes"
        :style="itemStyles">
        <label :class="[prefixCls + '-label']"
            :for="labelFor"
            :style="labelStyles"
            v-if="label || $slots.label">
            <slot name="label">{{ label }}</slot>
        </label>
        <div :class="[prefixCls + '-content']"
            :style="contentStyles">
            <slot></slot>
            <transition name="fade">
                <div v-if="finalShowMessage && isEndFormItem && validateState === 'error'"
                    :class="[prefixCls + '-error-tip']">
                    {{ validateMessage }}
                </div>
            </transition>
        </div>
    </div>
</template>
<script>
import Field from '../../mixins/field'

const prefixCls = 'ivu-form-item';

export default {
    name: 'FormItem',
    mixins: [Field],
    provide() {
        return {
            formItem: this,
        }
    },

    inject: {
        form: {
            from: "form",
            default: null,
        },
        parentFormItem: {
            from: "formItem",
            default: null,
        },
    },

    props: {
        value: {
            default: null,
        },
        label: {
            type: String,
            default: ''
        },
        labelWidth: {
            type: Number,
            default: null,
        },
        prop: {
            type: String
        },
        required: {
            type: Boolean,
            default: false
        },
        rules: {
            type: Object,
        },
        error: {
            type: String
        },
        showMessage: {
            type: Boolean,
            default: true
        },
        labelFor: {
            type: String
        },
        // 是否最后一排
        last: {
            type: Boolean,
            default: false
        },
        labelHeight:{
            type: Number,
            default: null,
        },
        // 内容高度
        height: {
            type: Number,
        },
        // 下边距
        marginBottom: {
            type: Number,
            default: null,
        },
        // 透明传输
        populateType: {
            type: String,
            default: "through",
        },
    },
    data() {
        return {
            prefixCls: prefixCls,
            formItemChildren: [],
        }
    },
    watch: {
        error: {
            handler(val) {
                // 立即校验
                this.validateDone(val ? { message: val } : [])
            },
            immediate: true
        },
        rules() {
            this.setRules()
        },
    },
    computed: {
        // FormItem会产生嵌套，某些场景需要区分是否末级
        isEndFormItem(){
            return this.formItemChildren.length <= 0
        },
        classes() {
            return [
                `${prefixCls}`,
                {
                    "is-last": this.last,
                    [`${prefixCls}-required`]: this.isRequired,
                    [`${prefixCls}-error`]: this.validateState === 'error' && this.isEndFormItem,
                    [`${prefixCls}-validating`]: this.validateState === 'validating'
                }
            ];
        },
        
        itemStyles() {
            let style = {},
                marginBottom = this.marginBottom

            if(marginBottom === null && this.form){
                marginBottom = this.form.marginBottom
            }

            if (marginBottom !== null) {
                style.marginBottom = `${marginBottom}px`
            }

            return style
        },

        finalLabelWidth(){
            if(this.labelWidth === null && this.form){
                return this.form.labelWidth
            }

            return this.labelWidth
        },

        finalShowMessage(){
            return this.showMessage || (!!this.form && this.form.showMessage)
        },

        labelStyles() {
            let style = {}

            // 宽度
            let labelWidth = this.finalLabelWidth

            if (labelWidth !== null) {
                style.width = `${labelWidth}px`
            }

            // 高度
            // 优先取自身
            let labelHeight = this.labelHeight

            // 其次取form.labelHeight
            if(labelHeight === null && this.form){
                labelHeight = this.form.labelHeight
            }

            // 其次取stack模式默认值
            if(labelHeight === null && this.form && this.form.labelPosition === "top"){
                labelHeight = 14
            }

            // 其次取form.itemHeight
            if(labelHeight === null && this.form){
                labelHeight = this.form.itemHeight
            }

            // style设置
            if (labelHeight !== null) {
                style.lineHeight = `${labelHeight}px`
            }

            if(this.form && this.form.labelPosition === "top"){
                style.textAlign = "left"
            }

            return style;
        },

        contentStyles() {
            let style = {};

            // 高度
            let itemHeight = this.height

            if(itemHeight === null && this.form){
                itemHeight = this.form.itemHeight
            }

            // 宽度
            let labelWidth = this.finalLabelWidth

            // stack模式无需label-width
            if(this.form && this.form.labelPosition == "top"){
                labelWidth = null
            }

            // style设置
            if (labelWidth !== null) {
                style.marginLeft = `${labelWidth}px`;
            }

            if (itemHeight !== null) {
                style.lineHeight = `${itemHeight}px`
            }

            return style;
        },

        isRequired(){
            var rules = this.finalRules[this.prop] || []

            return this.required || rules.some(v => v.required)
        },

        // override FormItem不承载数据，以直接子级为准
        fSpreadOut(){
            if(!this.isEndField){
                return this.fieldChildren[0].fSpreadOut
            }

            return this.spreadOut
        },
    },

    created() {
        if (this.parentFormItem && this.isComposed === false) {
            this.parentFormItem.addFormItem(this)
        }
    },

    beforeDestroy() {
        if (this.parentFormItem && this.isComposed === false) {
            this.parentFormItem.removeFormItem(this)
        }
    },

    mounted() {
        if (this.prop) {
            this.setRules()
        }
    },

    methods: {
        setRules() {
            this.$off('on-form-blur', this.onFieldBlur)
            this.$off('on-form-change', this.onFieldChange)

            this.$on('on-form-blur', this.onFieldBlur)
            this.$on('on-form-change', this.onFieldChange)
        },

        onFieldBlur() {
            this.validate('blur')
        },

        onFieldChange() {
            // 在v-model之后校验
            this.$nextTick(() => this.validate('change'))
        },

        addFormItem(vm){
            this.formItemChildren.push(vm)
        },
        
        removeFormItem(vm){
            var i = this.formItemChildren.indexOf(vm)
            
            if (i >= 0) {
                this.formItemChildren.splice(i, 1)
            }
        },
    },
};
</script>